import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, X, Siren } from "lucide-react";

let uid = 0;
const blankLine = () => ({ key: ++uid, pid: "", qty: "" });

export function EmergencyOrderDialog({ open, onOpenChange, onSaved }) {
  const [partners, setPartners] = useState([]);
  const [products, setProducts] = useState([]);
  const [buyer, setBuyer] = useState("");
  const [lines, setLines] = useState(() => [blankLine()]);
  const [date, setDate] = useState("");
  const [time, setTime] = useState("10:00");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setBuyer(""); setLines([blankLine()]); setNotes(""); setDate(new Date().toLocaleDateString("en-CA"));
    api.get("/partners").then((r) => setPartners(r.data.partners));
    api.get("/products").then((r) => setProducts((r.data.products || []).filter((p) => p.price != null)));
  }, [open]);
  const upd = (i, patch) => setLines(lines.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const submit = async () => {
    const items = lines.filter((l) => l.pid && Number(l.qty) > 0).map((l) => {
      const p = products.find((x) => x.id === l.pid);
      return { product_name: p.name, quantity: Number(l.qty), unit: p.unit };
    });
    if (!buyer || !items.length || !date || !time) { toast.error("Choose the partner, at least one product with quantity, and a delivery date & time"); return; }
    setBusy(true);
    try {
      const r = await api.post("/orders/emergency", { partner_business_id: buyer, items, requested_delivery_date: date, requested_delivery_time: time, notes: notes || null, fulfillment_type: "deliver" });
      toast.success(`Emergency order ${r.data.order.order_code} created`, { description: "The partner has been notified." });
      onSaved?.(); onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] w-[calc(100vw-1.5rem)] max-w-xl overflow-y-auto" data-testid="emergency-order-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Siren className="h-5 w-5 text-[#C85A32]" /> Emergency order for a partner</DialogTitle></DialogHeader>
        <p className="text-sm text-stone-500">For a partner who missed the ordering window. It's labelled Emergency, logged under your name, and arrives as a New order.</p>
        <div><Label>Business partner *</Label>
          <Select value={buyer} onValueChange={setBuyer}>
            <SelectTrigger className="mt-1" data-testid="emergency-partner-select"><SelectValue placeholder="Select partner" /></SelectTrigger>
            <SelectContent>{partners.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} ({p.profile_code})</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Products & quantities *</Label>
          {lines.map((l, i) => (
            <div key={l.key} className="grid grid-cols-[1fr_110px_auto] gap-2">
              <Select value={l.pid} onValueChange={(v) => upd(i, { pid: v })}>
                <SelectTrigger data-testid={`emergency-product-${i}`}><SelectValue placeholder="Product" /></SelectTrigger>
                <SelectContent>{products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {p.unit}</SelectItem>)}</SelectContent>
              </Select>
              <Input type="number" min="0" step="any" placeholder="Qty" value={l.qty} onChange={(e) => upd(i, { qty: e.target.value })} data-testid={`emergency-qty-${i}`} />
              <Button type="button" variant="ghost" size="sm" disabled={lines.length === 1} onClick={() => setLines(lines.filter((_, j) => j !== i))} data-testid={`emergency-remove-line-${i}`}><X className="h-4 w-4" /></Button>
            </div>
          ))}
          <Button type="button" variant="outline" size="sm" onClick={() => setLines([...lines, blankLine()])} data-testid="emergency-add-line"><Plus className="mr-1 h-4 w-4" /> Add product</Button>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div><Label>Delivery date *</Label><Input type="date" className="mt-1" value={date} onChange={(e) => setDate(e.target.value)} data-testid="emergency-date" /></div>
          <div><Label>Delivery time *</Label><Input type="time" className="mt-1" value={time} onChange={(e) => setTime(e.target.value)} data-testid="emergency-time" /></div>
        </div>
        <Input placeholder="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="emergency-notes" />
        <DialogFooter><Button disabled={busy} onClick={submit} className="bg-[#C85A32] hover:bg-[#a8481f]" data-testid="emergency-submit">Create emergency order</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
